import { BusinessObjectBase, ModelBase, QueryBus } from '@skylab-shared/core';
import { InventoryDocumentKind, SaleDocumentKind } from '@skylab-shared/dataModel';
import { ContextKeys } from '../../common/context-keys';
import { DocumentLink, DocumentLinks, DocumentLinksQuery } from '../../documents/queries/document-links.query-base';

interface ModelPart extends ModelBase {
    id: number;
    kind: SaleDocumentKind;
}

export abstract class SaleLinksMixin<TModel extends ModelPart> extends BusinessObjectBase<TModel>  {
    protected queryBus: QueryBus;

    public async loadLinksAsync(): Promise<void> {
        this.internalLinks = await this.localContext.getAsync(ContextKeys.documentLinks(this.model.id), () => this.queryBus.queryAsync(new DocumentLinksQuery(this.model.id)));
        this.internalAdjustments = this.internalLinks.adjustments;
        this.internalImplemented = this.internalLinks.implemented;
        this.internalImplementing = this.internalLinks.implementing;
        this.internalInventory = this.internalLinks.inventory;
        this.internalCurrent = this.internalLinks.current;

        if (this.internalAdjustments && this.model.kind === SaleDocumentKind.ReceiptAdjustment) {
            this.internalOriginalAdjustedDocumentLinks = await this.queryBus.queryAsync(new DocumentLinksQuery(this.internalAdjustments[0].documentId));
        }

        if (this.internalImplemented && this.model.kind === SaleDocumentKind.Invoice) {
            const originalReceiptDocument = this.internalImplemented.find(i => i.kind === SaleDocumentKind.Receipt);
            if (originalReceiptDocument) {
                this.internalOriginalReceiptDocumentLinks = await this.queryBus.queryAsync(new DocumentLinksQuery(originalReceiptDocument.documentId));
            }
        }
    }

    //#region main getters
    private internalOriginalAdjustedDocumentLinks: DocumentLinks;
    public get originalAdjustedDocumentLinks(): DocumentLinks {
        return this.internalOriginalAdjustedDocumentLinks;
    }

    private internalOriginalReceiptDocumentLinks: DocumentLinks;
    public get originalReceiptDocumentLinks(): DocumentLinks {
        return this.internalOriginalReceiptDocumentLinks;
    }

    private internalLinks: DocumentLinks;
    public get links(): DocumentLinks {
        return this.internalLinks;
    }

    private internalCurrent: DocumentLink;
    public get linksCurrent(): DocumentLink {
        return this.internalCurrent;
    }

    private internalAdjustments: DocumentLink[] | null | undefined;
    public get linksAdjustments(): DocumentLink[] | null | undefined {
        return this.internalAdjustments;
    }

    private internalImplemented: DocumentLink[] | null | undefined;
    public get linksImplemented(): DocumentLink[] | null | undefined {
        return this.internalImplemented;
    }

    private internalImplementing: DocumentLink[] | null | undefined;
    public get linksImplementing(): DocumentLink[] | null | undefined {
        return this.internalImplementing;
    }

    private internalInventory: DocumentLink[] | null | undefined;
    public get linksInventory(): DocumentLink[] | null | undefined {
        return this.internalInventory;
    }
    //#endregion

    //#region statuses
    public get linksAdjusted(): boolean {
        return this.linksLastAdjustment ? this.linksLastAdjustment.documentId != this.model.id : false;
    }

    public get linksReceiptWithIssuedInvoice(): boolean {
        const adjustmentToReceiptWithIssuedInvoice =
            this.model.kind === SaleDocumentKind.ReceiptAdjustment &&
            this.internalOriginalAdjustedDocumentLinks &&
            this.internalOriginalAdjustedDocumentLinks.implementing &&
            this.internalOriginalAdjustedDocumentLinks.implementing.find(i => i.kind === SaleDocumentKind.Invoice) != null;

        const receiptWithIssuedInvoice =
            this.model.kind === SaleDocumentKind.Receipt &&
            this.linksImplementing &&
            this.linksImplementing.find(i => i.kind === SaleDocumentKind.Invoice) != null;

        return !!(adjustmentToReceiptWithIssuedInvoice || receiptWithIssuedInvoice);
    }

    public get linksInvoiceIssuedToReceipt(): boolean {
        return !!(
            this.model.kind === SaleDocumentKind.Invoice &&
            this.linksImplemented &&
            this.linksImplemented.find(i => i.kind === SaleDocumentKind.Receipt || i.kind === SaleDocumentKind.ReceiptAdjustment) != null);
    }

    public get linksOrderedProForma(): boolean {
        return !!(
            this.model.kind === SaleDocumentKind.ProForma &&
            this.linksImplementing != null);
    }

    public get linksInvoiceIssuedToInventoryGoodsIssue(): boolean {
        return !!this.linksInventory?.some(elem => elem.kind === InventoryDocumentKind.GoodsIssue);
    }
    //#endregion

    //#region helper getters
    public get linksLastAdjustment(): DocumentLink | null {
        return this.linksAdjustments ? this.linksAdjustments[this.linksAdjustments.length - 1] : null;
    }

    public get linksPreviouslyAdjusted(): DocumentLink | null {
        return this.linksAdjustments ? this.linksAdjustments.filter((a, idx, array) => array[idx + 1] && array[idx + 1].documentId === this.model.id)[0] : null;
    }

    public get linksInvoiceIssuedToReceiptSourceLink(): DocumentLink | null {
        return this.originalReceiptDocumentLinks && this.originalReceiptDocumentLinks.adjustments
            ? this.originalReceiptDocumentLinks.adjustments[this.originalReceiptDocumentLinks.adjustments.length - 1]
            : this.linksImplemented ? this.linksImplemented.find(i => i.kind === SaleDocumentKind.Receipt || i.kind === SaleDocumentKind.ReceiptAdjustment) ?? null : null;
    }
    //#endregion

}
